import { useState } from "react";

import { faFilter } from "@fortawesome/pro-regular-svg-icons";
import { faFilter as faFilterSolid } from "@fortawesome/pro-solid-svg-icons";
import {
  CloseButton,
  Popover,
  PopoverButton,
  PopoverPanel,
} from "@headlessui/react";

import { Button } from "src/design-system/Button";
import { Dropdown } from "src/design-system/Dropdown";
import { Icon } from "src/design-system/Icon";

type Props = {
  dataLoc?: string;
  isOpen?: boolean;
  description: string;
  children: React.ReactNode;
  isFiltering: boolean;
  isApplied?: boolean;
  onResetRequest: () => void;
  onApply: () => void;
  value: string;
  operator: string;
};

export const CustomDropdownFilter = ({
  dataLoc,
  description,
  children,
  isFiltering,
  isApplied,
  onResetRequest,
  onApply,
  value,
  operator,
}: Props) => {
  const [lastAppliedValue, setLastAppliedValue] = useState<string>(value);
  const [lastAppliedOperator, setLastAppliedOperator] =
    useState<string>(operator);

  const isApplyDisabled =
    value === lastAppliedValue && operator === lastAppliedOperator;
  const isResetDisabled = !isFiltering || value === "";

  return (
    <Popover className="relative">
      <PopoverButton className="border-0 bg-transparent" data-loc={dataLoc}>
        {({ open, hover }) => (
          <Icon
            variant={
              open || isFiltering ? "active" : hover ? "hover" : "default"
            }
            icon={open || isFiltering ? faFilterSolid : faFilter}
            size="2xs"
          />
        )}
      </PopoverButton>

      <PopoverPanel
        anchor={{
          to: "bottom end",
          gap: 10,
          padding: 12,
        }}
        as={Dropdown}
        data-loc={`${dataLoc}-dropdown`}
        transition
      >
        <div className="divide-y divide-gray-200">
          <div className="flex w-[240px] flex-col justify-between p-4">
            <h3 className="cursor-default items-center font-inter-semibold-13px">
              {description}
            </h3>
            <div className="pt-2">{children}</div>
          </div>

          <div className="flex justify-between p-4">
            <CloseButton
              as={Button}
              isDisabled={isResetDisabled}
              size="sm"
              variant="secondary"
              onClick={onResetRequest}
            >
              Reset
            </CloseButton>
            <CloseButton
              as={Button}
              dataLoc={`${dataLoc}-apply-button`}
              isDisabled={isApplyDisabled || isApplied}
              size="sm"
              variant="primary"
              onClick={() => {
                setLastAppliedValue(value);
                setLastAppliedOperator(operator);
                onApply();
              }}
            >
              Apply
            </CloseButton>
          </div>
        </div>
      </PopoverPanel>
    </Popover>
  );
};
